(() => {
	'use strict';

	// Store all slideshow instances
	const slideshowInstances = new Map();
	
	// Check for reduced motion preference
	const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	
	// Utility functions
	const utils = {
		// Debounce function for performance
		debounce(func, wait) {
			let timeout;
			return function executedFunction(...args) {
				const later = () => {
					clearTimeout(timeout);
					func(...args);
				};
				clearTimeout(timeout);
				timeout = setTimeout(later, wait);
			};
		},

		// Focus management
		focusElement(element) {
			if (element && typeof element.focus === 'function') {
				element.focus();
			}
		},

		// Announce to screen readers
		announceToScreenReader(message, priority = 'polite') {
			const announcement = document.createElement('div');
			announcement.setAttribute('aria-live', priority);
			announcement.setAttribute('aria-atomic', 'true');
			announcement.className = 'visually-hidden';
			announcement.textContent = message;
			
			document.body.appendChild(announcement);
			
			setTimeout(() => {
				document.body.removeChild(announcement);
			}, 1000);
		},

		// Get slide description for screen readers
		getSlideDescription(slide) {
			const heading = slide.querySelector('.slideshow__heading');
			const description = slide.querySelector('.slideshow__description');
			
			let desc = '';
			if (heading) desc += heading.textContent.trim();
			if (description) desc += (desc ? ': ' : '') + description.textContent.trim();
			
			return desc || 'Slide content';
		}
	};

	// Main slideshow class
	class AccessibleSlideshow {
		constructor(container) {
			this.container = container;
			this.swiper = null;
			this.isAutoplayActive = false;
			this.autoplayButton = null;
			this.slides = [];
			this.currentSlideIndex = 0;
			
			this.init();
		}

		init() {
			try {
				this.parseConfig();
				this.createSwiper();
				this.setupAccessibility();
				this.setupEventListeners();
				this.setupKeyboardNavigation();
				this.setupAutoplayToggle();
				this.ensureControlsVisibility();
				
				// Store instance
				const sectionId = this.container.closest('.slideshow').dataset.sectionId;
				slideshowInstances.set(sectionId, this);
				
			} catch (error) {
				console.error('Slideshow initialization failed:', error);
			}
		}

		parseConfig() {
			const configAttr = this.container.getAttribute('data-swiper-config');
			if (!configAttr) {
				throw new Error('No swiper config found');
			}
			
			this.config = JSON.parse(configAttr);
			this.slides = Array.from(this.container.querySelectorAll('.swiper-slide'));
			this.hasMultipleSlides = this.slides.length > 1;
			
			// Respect reduced motion
			if (prefersReducedMotion) {
				this.config.autoplay = false;
				this.config.speed = 0;
				this.config.effect = 'slide';
			}
		}

		createSwiper() {
			// Enhanced config for accessibility
			const accessibleConfig = {
				...this.config,
				
				// Callbacks for accessibility and functionality
				on: {
					init: (swiper) => this.onSwiperInit(swiper),
					slideChange: (swiper) => this.onSlideChange(swiper),
					slideChangeTransitionStart: (swiper) => this.onSlideChangeStart(swiper),
					slideChangeTransitionEnd: (swiper) => this.onSlideChangeEnd(swiper),
					autoplayStart: () => this.onAutoplayStart(),
					autoplayStop: () => this.onAutoplayStop(),
					autoplayPause: () => this.onAutoplayPause(),
					autoplayResume: () => this.onAutoplayResume(),
					touchStart: () => this.onTouchStart(),
					touchEnd: () => this.onTouchEnd()
				}
			};

			// Create Swiper instance
			this.swiper = new Swiper(this.container, accessibleConfig);
		}

		setupAccessibility() {
			// Set up proper ARIA attributes
			this.container.setAttribute('tabindex', this.hasMultipleSlides ? '0' : '-1');
			
			// Setup slide accessibility
			this.slides.forEach((slide, index) => {
				slide.setAttribute('aria-posinset', index + 1);
				slide.setAttribute('aria-setsize', this.slides.length);
				
				// Handle focusable elements in inactive slides
				if (index !== 0) {
					this.setSlideTabIndex(slide, false);
				}
			});

			// Setup control accessibility
			this.setupControlsAccessibility();
		}

		setupControlsAccessibility() {
			const prevButton = this.container.querySelector('.swiper-button-prev');
			const nextButton = this.container.querySelector('.swiper-button-next');
			const pagination = this.container.querySelector('.swiper-pagination');

			if (prevButton) {
				prevButton.setAttribute('aria-label', 'Previous slide');
				prevButton.setAttribute('tabindex', '0');
			}

			if (nextButton) {
				nextButton.setAttribute('aria-label', 'Next slide');
				nextButton.setAttribute('tabindex', '0');
			}

			if (pagination) {
				pagination.setAttribute('role', 'tablist');
				pagination.setAttribute('aria-label', `Slide navigation - ${this.slides.length} slides total`);
			}
		}

		ensureControlsVisibility() {
			// Ensure controls are visible from the start
			const controls = this.container.querySelector('.slideshow__controls');
			if (controls) {
				controls.style.opacity = '1';
				controls.style.visibility = 'visible';
			}

			// Force update navigation and pagination visibility
			if (this.swiper && this.hasMultipleSlides) {
				setTimeout(() => {
					if (this.swiper.navigation) {
						this.swiper.navigation.update();
					}
					if (this.swiper.pagination) {
						this.swiper.pagination.update();
					}
				}, 100);
			}
		}

		setupEventListeners() {
			// Handle focus events
			this.container.addEventListener('focus', (e) => this.onFocus(e));
			this.container.addEventListener('blur', (e) => this.onBlur(e));
			
			// Handle mouse events for autoplay
			if (this.config.autoplay && this.config.autoplay.pauseOnMouseEnter) {
				this.container.addEventListener('mouseenter', () => this.pauseAutoplay());
				this.container.addEventListener('mouseleave', () => this.resumeAutoplay());
			}

			// Handle visibility change
			document.addEventListener('visibilitychange', () => {
				if (document.hidden) {
					this.pauseAutoplay();
				} else {
					this.resumeAutoplay();
				}
			});

			// Handle window resize
			window.addEventListener('resize', utils.debounce(() => {
				if (this.swiper) {
					this.swiper.update();
				}
			}, 250));
		}

		setupKeyboardNavigation() {
			if (!this.hasMultipleSlides) return;

			this.container.addEventListener('keydown', (e) => {
				// Don't interfere if user is typing in an input
				if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
					return;
				}

				switch (e.key) {
					case 'ArrowLeft':
					case 'ArrowUp':
						e.preventDefault();
						this.swiper.slidePrev();
						utils.announceToScreenReader('Previous slide');
						break;
						
					case 'ArrowRight':
					case 'ArrowDown':
						e.preventDefault();
						this.swiper.slideNext();
						utils.announceToScreenReader('Next slide');
						break;
						
					case 'Home':
						e.preventDefault();
						this.swiper.slideTo(0);
						utils.announceToScreenReader('First slide');
						break;
						
					case 'End':
						e.preventDefault();
						this.swiper.slideTo(this.slides.length - 1);
						utils.announceToScreenReader('Last slide');
						break;
						
					case ' ':
					case 'Enter':
						if (this.config.autoplay && e.target === this.container) {
							e.preventDefault();
							this.toggleAutoplay();
						}
						break;
				}
			});
		}

		setupAutoplayToggle() {
			if (!this.config.autoplay) return;

			// Look for the accessible autoplay button first (positioned before slideshow)
			this.autoplayButton = document.querySelector('.slideshow__autoplay-toggle--accessible');
			
			if (!this.autoplayButton) return;

			this.autoplayButton.addEventListener('click', () => this.toggleAutoplay());
			this.autoplayButton.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					this.toggleAutoplay();
				}
			});

			// Set initial state
			this.updateAutoplayButton();
		}

		// Swiper event handlers
		onSwiperInit(swiper) {
			this.isAutoplayActive = swiper.autoplay && swiper.autoplay.running;
			this.updateAutoplayButton();
			
			// Focus management
			if (this.hasMultipleSlides) {
				this.setSlideTabIndex(this.slides[0], true);
			}

			// Ensure controls are visible after init
			setTimeout(() => this.ensureControlsVisibility(), 50);
		}

		onSlideChange(swiper) {
			this.currentSlideIndex = swiper.realIndex;
			
			// Update pagination accessibility
			this.updatePaginationAccessibility();
			
			// Announce slide change to screen readers
			const currentSlide = this.slides[this.currentSlideIndex];
			const slideDescription = utils.getSlideDescription(currentSlide);
			const announcement = `Slide ${this.currentSlideIndex + 1} of ${this.slides.length}: ${slideDescription}`;
			
			utils.announceToScreenReader(announcement);
		}

		onSlideChangeStart(swiper) {
			// Manage focus for slide transitions
			this.slides.forEach((slide, index) => {
				this.setSlideTabIndex(slide, index === swiper.realIndex);
			});
		}

		onSlideChangeEnd(swiper) {
			// Ensure proper focus management
			const activeSlide = this.slides[swiper.realIndex];
			if (activeSlide && document.activeElement === this.container) {
				// Focus the active slide content if slideshow container had focus
				const focusableElement = activeSlide.querySelector('a, button, [tabindex="0"]');
				if (focusableElement) {
					utils.focusElement(focusableElement);
				}
			}
		}

		onAutoplayStart() {
			this.isAutoplayActive = true;
			this.updateAutoplayButton();
		}

		onAutoplayStop() {
			this.isAutoplayActive = false;
			this.updateAutoplayButton();
		}

		onAutoplayPause() {
			this.isAutoplayActive = false;
			this.updateAutoplayButton();
		}

		onAutoplayResume() {
			this.isAutoplayActive = true;
			this.updateAutoplayButton();
		}

		onTouchStart() {
			// Provide haptic feedback on supported devices
			if (navigator.vibrate) {
				navigator.vibrate(50);
			}
		}

		onTouchEnd() {
			// Announce touch interaction completed
			utils.announceToScreenReader('Slide changed', 'assertive');
		}

		// Focus management methods
		onFocus(e) {
			if (this.config.autoplay && this.isAutoplayActive) {
				this.pauseAutoplay();
			}
		}

		onBlur(e) {
			// Resume autoplay when focus leaves slideshow
			if (this.config.autoplay && !this.container.contains(e.relatedTarget)) {
				this.resumeAutoplay();
			}
		}

		setSlideTabIndex(slide, isActive) {
			const focusableElements = slide.querySelectorAll('a, button, [tabindex]');
			focusableElements.forEach(element => {
				if (isActive) {
					// Restore original tabindex or set to 0
					const originalTabIndex = element.dataset.originalTabindex;
					element.tabIndex = originalTabIndex ? parseInt(originalTabIndex) : 0;
				} else {
					// Store original tabindex and set to -1
					if (!element.dataset.originalTabindex) {
						element.dataset.originalTabindex = element.tabIndex;
					}
					element.tabIndex = -1;
				}
			});
		}

		updatePaginationAccessibility() {
			const pagination = this.container.querySelector('.swiper-pagination');
			if (!pagination) return;

			const bullets = pagination.querySelectorAll('.swiper-pagination-bullet');
			bullets.forEach((bullet, index) => {
				bullet.setAttribute('role', 'tab');
				bullet.setAttribute('aria-selected', index === this.currentSlideIndex ? 'true' : 'false');
				bullet.setAttribute('aria-label', `Go to slide ${index + 1}`);
				bullet.setAttribute('tabindex', index === this.currentSlideIndex ? '0' : '-1');
			});
		}

		// Autoplay methods
		toggleAutoplay() {
			if (!this.swiper || !this.config.autoplay) return;

			if (this.isAutoplayActive) {
				this.pauseAutoplay();
			} else {
				this.resumeAutoplay();
			}
		}

		pauseAutoplay() {
			if (this.swiper && this.swiper.autoplay && this.isAutoplayActive) {
				this.swiper.autoplay.stop();
				utils.announceToScreenReader('Slideshow paused');
			}
		}

		resumeAutoplay() {
			if (this.swiper && this.swiper.autoplay && this.config.autoplay && !this.isAutoplayActive) {
				this.swiper.autoplay.start();
				utils.announceToScreenReader('Slideshow resumed');
			}
		}

		updateAutoplayButton() {
			if (!this.autoplayButton) return;

			const isPlaying = this.isAutoplayActive;
			const playText = this.autoplayButton.dataset.playText || 'Play slideshow';
			const pauseText = this.autoplayButton.dataset.pauseText || 'Pause slideshow';

			this.autoplayButton.setAttribute('aria-label', isPlaying ? pauseText : playText);
			this.autoplayButton.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
			this.autoplayButton.setAttribute('title', isPlaying ? pauseText : playText);

			const textElement = this.autoplayButton.querySelector('.slideshow__autoplay-text');
			if (textElement) {
				textElement.textContent = isPlaying ? pauseText : playText;
			}

			const pauseIcon = this.autoplayButton.querySelector('.slideshow__autoplay-icon--pause');
			const playIcon = this.autoplayButton.querySelector('.slideshow__autoplay-icon--play');

			if (pauseIcon) pauseIcon.style.display = isPlaying ? 'inline' : 'none';
			if (playIcon) playIcon.style.display = isPlaying ? 'none' : 'inline';
		}

		// Public methods
		destroy() {
			if (this.swiper) {
				this.swiper.destroy(true, true);
				this.swiper = null;
			}
			
			const sectionId = this.container.closest('.slideshow').dataset.sectionId;
			slideshowInstances.delete(sectionId);
		}

		update() {
			if (this.swiper) {
				this.swiper.update();
				this.ensureControlsVisibility();
			}
		}
	}

	// Initialize slideshows
	const initSlideshows = () => {
		const slideshows = document.querySelectorAll('.slideshow__swiper');
		
		slideshows.forEach(slideshow => {
			// Skip if already initialized
			const sectionId = slideshow.closest('.slideshow').dataset.sectionId;
			if (slideshowInstances.has(sectionId)) {
				return;
			}

			try {
				new AccessibleSlideshow(slideshow);
			} catch (error) {
				console.error('Failed to initialize slideshow:', error);
			}
		});
	};

	// Cleanup function
	const cleanupSlideshows = () => {
		slideshowInstances.forEach(instance => {
			instance.destroy();
		});
		slideshowInstances.clear();
	};

	// Initialize when DOM is ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initSlideshows);
	} else {
		initSlideshows();
	}

	// Handle Shopify theme editor
	document.addEventListener('shopify:section:load', (e) => {
		const section = e.target;
		const slideshow = section.querySelector('.slideshow__swiper');
		if (slideshow) {
			setTimeout(() => {
				new AccessibleSlideshow(slideshow);
			}, 100);
		}
	});

	document.addEventListener('shopify:section:unload', (e) => {
		const section = e.target;
		const sectionId = section.dataset.sectionId;
		const instance = slideshowInstances.get(sectionId);
		if (instance) {
			instance.destroy();
		}
	});

	// Handle page visibility for autoplay
	document.addEventListener('visibilitychange', () => {
		slideshowInstances.forEach(instance => {
			if (document.hidden) {
				instance.pauseAutoplay();
			} else {
				instance.resumeAutoplay();
			}
		});
	});

	// Cleanup on page unload
	window.addEventListener('beforeunload', cleanupSlideshows);

	// Export for debugging
	window.SlideshowInstances = slideshowInstances;

})();
